import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Trophy, MapPin, Calendar, Ruler, Scale, Fish, ArrowLeft, Edit, Shield } from "lucide-react";
import { format } from "date-fns";
import logo from "@/assets/logo.png";
import { useAuth } from "@/contexts/AuthContext";

const fetchPublicCatch = async (catchId: string | undefined) => {
  if (!catchId) throw new Error("No catch ID provided");

  const { data, error } = await supabase
    .from("catches")
    .select(`
      *,
      profiles:angler_id (
        angler_name,
        nickname,
        avatar_url
      )
    `)
    .eq("id", catchId)
    .eq("status", "verified")
    .single();

  if (error) throw error;
  if (!data) throw new Error("Catch not found");
  return data;
};

export default function PublicCatch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check if current user is admin
  const { data: isAdmin } = useQuery({
    queryKey: ["is-admin", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data } = await supabase.rpc("has_role", {
        _user_id: user.id,
        _role: "admin",
      });
      return data || false;
    },
    enabled: !!user?.id,
  });

  const { data: catch_, isLoading, error } = useQuery({
    queryKey: ["public-catch", id],
    queryFn: () => fetchPublicCatch(id),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container py-8 max-w-2xl">
          <Skeleton className="h-12 w-48 mb-8" />
          <Card>
            <Skeleton className="h-64 w-full" />
            <CardContent className="space-y-4 pt-6">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error || !catch_) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="pt-6 text-center space-y-4">
            <Fish className="h-16 w-16 text-muted-foreground mx-auto" />
            <div>
              <h3 className="text-lg font-semibold mb-2">Catch Not Found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                This catch doesn't exist or hasn't been verified yet.
              </p>
              <Button onClick={() => navigate("/")} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const anglerName = catch_.profiles?.nickname || catch_.profiles?.angler_name || "Anonymous Angler";
  const isOwner = user?.id === catch_.angler_id;
  const canEdit = isOwner || isAdmin;
  const showAdminInfo = isAdmin;
  const isAuthenticated = !!user;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="bg-background/95 backdrop-blur border-b">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Upper Room Fishing Club" className="h-10 w-auto" />
              <span className="text-xl font-bold text-gradient hidden sm:inline">
                Upper Room Fishing Club
              </span>
            </div>
            <Button onClick={() => navigate("/")} variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Home
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-8 max-w-3xl">
        <Card className="overflow-hidden shadow-2xl">
          {/* Hero Image */}
          {catch_.photo_url && (
            <div className="relative aspect-video w-full overflow-hidden bg-muted">
              <img
                src={catch_.photo_url}
                alt={catch_.fish_species}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 right-4 flex gap-2">
                <Badge className="bg-success text-success-foreground">
                  <Trophy className="h-3 w-3 mr-1" />
                  Verified
                </Badge>
                {showAdminInfo && (
                  <Badge variant="secondary" className="bg-purple-500 text-white">
                    <Shield className="h-3 w-3 mr-1" />
                    Admin View
                  </Badge>
                )}
              </div>
            </div>
          )}

          <CardContent className="p-6 md:p-8 space-y-6">
            {/* Angler Info */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16 border-2 border-primary">
                  <AvatarImage src={catch_.profiles?.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                    {anglerName.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm text-muted-foreground">Caught by</p>
                  <p className="text-xl font-bold">{anglerName}</p>
                  {isOwner && (
                    <Badge variant="outline" className="mt-1">Your Catch</Badge>
                  )}
                </div>
              </div>
              {canEdit && (
                <Button
                  onClick={() => navigate(`/edit-catch/${catch_.id}`)}
                  variant="outline"
                  size="sm"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>

            {/* Main Stats */}
            <div className="border-t border-b py-6">
              <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-2">
                {catch_.fish_species}
              </h1>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>{format(new Date(catch_.date_caught), "MMMM d, yyyy")}</span>
              </div>
            </div>

            {/* Weight & Measurements Grid */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Scale className="h-4 w-4" />
                  <span>Weight</span>
                </div>
                <p className="text-3xl font-bold text-primary">
                  {catch_.weight_lbs} <span className="text-xl">lbs</span>
                </p>
              </div>

              {catch_.length_inches && (
                <div className="p-4 rounded-lg bg-accent/5 border border-accent/20">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Ruler className="h-4 w-4" />
                    <span>Length</span>
                  </div>
                  <p className="text-3xl font-bold text-accent">
                    {catch_.length_inches} <span className="text-xl">in</span>
                  </p>
                </div>
              )}

              {catch_.girth_inches && (
                <div className="p-4 rounded-lg bg-secondary/5 border border-secondary/20">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Ruler className="h-4 w-4" />
                    <span>Girth</span>
                  </div>
                  <p className="text-3xl font-bold">
                    {catch_.girth_inches} <span className="text-xl">in</span>
                  </p>
                </div>
              )}
            </div>

            {/* Location & Details */}
            <div className="space-y-3">
              {catch_.general_location && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-semibold">Location</p>
                    <p className="text-muted-foreground">{catch_.general_location}</p>
                  </div>
                </div>
              )}

              {catch_.boat_name && (
                <div className="flex items-start gap-3">
                  <Fish className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-semibold">Vessel</p>
                    <p className="text-muted-foreground">{catch_.boat_name}</p>
                    {catch_.captain_name && (
                      <p className="text-sm text-muted-foreground">
                        Captain: {catch_.captain_name}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {catch_.line_test && (
                <div className="flex items-start gap-3">
                  <Fish className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-semibold">Line Test</p>
                    <p className="text-muted-foreground">{catch_.line_test}</p>
                  </div>
                </div>
              )}

              {catch_.angler_notes && (
                <div className="flex items-start gap-3">
                  <Fish className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-semibold">Notes</p>
                    <p className="text-muted-foreground">{catch_.angler_notes}</p>
                  </div>
                </div>
              )}

              {/* Admin-only information */}
              {showAdminInfo && (
                <>
                  {catch_.admin_notes && (
                    <div className="flex items-start gap-3 p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
                      <Shield className="h-5 w-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-purple-900 dark:text-purple-100">Admin Notes</p>
                        <p className="text-purple-700 dark:text-purple-300">{catch_.admin_notes}</p>
                      </div>
                    </div>
                  )}
                  {isAuthenticated && (
                    <div className="p-4 bg-muted rounded-lg space-y-2 text-sm">
                      <p className="font-semibold">Additional Details (Admin Only)</p>
                      {catch_.time_hooked && (
                        <p>Time Hooked: {catch_.time_hooked}</p>
                      )}
                      {catch_.time_to_land && (
                        <p>Time to Land: {catch_.time_to_land} minutes</p>
                      )}
                      {catch_.weighmaster_name && (
                        <p>Weighmaster: {catch_.weighmaster_name}</p>
                      )}
                      {catch_.weigh_method && (
                        <p>Weigh Method: {catch_.weigh_method}</p>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Authenticated users see more details */}
              {isAuthenticated && !showAdminInfo && catch_.time_to_land && (
                <div className="flex items-start gap-3">
                  <Fish className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-semibold">Fight Time</p>
                    <p className="text-muted-foreground">{catch_.time_to_land} minutes</p>
                  </div>
                </div>
              )}
            </div>

            {/* CTA - Only show for non-authenticated users */}
            {!isAuthenticated && (
              <div className="pt-6 border-t text-center space-y-4">
                <p className="text-sm text-muted-foreground">
                  Want to share your own catches?
                </p>
                <Button onClick={() => navigate("/auth")} size="lg" className="ocean-gradient">
                  Join Upper Room Fishing Club
                </Button>
              </div>
            )}

            {/* Authenticated users see dashboard link */}
            {isAuthenticated && !isOwner && (
              <div className="pt-6 border-t text-center">
                <Button onClick={() => navigate("/dashboard")} variant="outline">
                  View Your Catches
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="container py-8 text-center text-sm text-muted-foreground">
        <p>© {new Date().getFullYear()} Upper Room Fishing Club. All rights reserved.</p>
      </footer>
    </div>
  );
}
